import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentThreadToolDetailResponse } from "../AgentThreadRecord";

export function getQueryKey(threadId?: string, toolUseId?: string) {
  // Always include both identifiers (``null`` standing in for an absent one) so
  // every (thread, tool) pair gets a distinct cache entry. Collapsing the
  // not-fetchable case to a bare ``["agentThreadToolDetails"]`` would make every
  // chip without a ``toolUseId`` share one entry across all threads — harmless
  // while the query stays ``enabled: false``, but a cache-bleed hazard the
  // moment any path writes to it.
  return ["agentThreadToolDetails", threadId ?? null, toolUseId ?? null];
}

export function useToolDetails(
  threadId: string | undefined,
  toolUseId: string | undefined,
): UseQueryResult<AgentThreadToolDetailResponse, Error> {
  const { agentThreadService } = useDomainServices();

  return useQuery<AgentThreadToolDetailResponse, Error>({
    queryKey: getQueryKey(threadId, toolUseId),
    queryFn: ({ signal }) => {
      if (threadId === undefined || toolUseId === undefined) {
        return Promise.reject(new Error("Invalid threadId or toolUseId"));
      }

      return agentThreadService.getToolDetails(threadId, toolUseId, {
        abortSignal: signal,
      });
    },
    enabled: false,
  });
}
